import React, { useMemo, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Spinner, Alert, Badge } from 'react-bootstrap';
import { FaChartBar, FaSync, FaTimes, FaCheck, FaCog, FaBuilding, FaHandshake, FaFileContract, FaCaretUp, FaCaretDown, FaPrint, FaMoneyBillWave } from 'react-icons/fa';
import PrintModal from './components/PrintModal';
import PrintLayout from './components/PrintLayout';
import Select from 'react-select';
import '../../styles/Decisionnel.css';

// secUnit / unitPick : sélecteur d'unité monétaire par section (Auto/MAD/kMAD/MMAD)

import { useDecisionnel } from './hooks/useDecisionnel';
import { C, SEC_LABELS, STATUT_NOTAIRE_OPTIONS, selectStyles, inputStyle, getBienTypeColor } from './utils/constants';
import { fmt, fmtM, fmtUnit, fmtLabel, toOpt, toOptStr } from './utils/helpers';
import { exportExcel, rowsToHtml, copyHtmlToClipboard } from './utils/exports';

import { XBtn, TableExportBar, SvgExportBox, SectionWrapper, UnitPicker } from './shared/index';
import BarChart from './charts/BarChart';
import CumulChart from './charts/CumulChart';
import TableauMensuel from './tables/TableauMensuel';
import TableauSynthese from './tables/TableauSynthese';
import TableauStockDestockage from './tables/TableauStockDestockage';
import DetailTableVentes from './tables/DetailTableVentes';
import DetailTableRec from './tables/DetailTableRec';
import DetailTablePn from './tables/DetailTablePn';
import KpiDetailPanel from './components/KpiDetailPanel';
import ComboKpiCard from './components/ComboKpiCard';
import RecouvrementSection from './components/RecouvrementSection';
import { ProjetKpiCard, StackedBarProjet } from './components/ProjetSection';
import ObjectifCommercialSection from './components/ObjectifCommercialSection';
import DureeTraitementSection from './components/DureeTraitementSection';
import NotaireSoonSection from './components/NotaireSoonSection';
import StatutLotsSection  from './components/StatutLotsSection';
import CommissionSection   from './components/CommissionSection';
import SituationEncoursSection      from './components/SituationEncoursSection';
import SituationCommercialeSection  from './components/SituationCommercialeSection';

const TITRE_FONCIER_OPTIONS = [
  { value: '1', label: 'Avec TF' },
  { value: '0', label: 'Sans TF' },
  { value: '2', label: 'Prochainement' },
];

const SECTION_PRIV = {
  'kpi':             'decisionnel:kpi',
  'notaire-soon':    'decisionnel:notaire',
  'statut-lots':     'decisionnel:lots',
  'situation-encours':'decisionnel:lots',
  'commissions':     'decisionnel:lots',
  'projets':         'decisionnel:projets',
  'ca-projet':       'decisionnel:projets',
  'commerciaux':     'decisionnel:commerciaux',
  'graphiques':      'decisionnel:graphiques',
  'analyse-rec':     'decisionnel:recouvrement',
  'synthese':        'decisionnel:recouvrement',
  'stock':           'decisionnel:recouvrement',
  'tableau':         'decisionnel:tableau',
  'detail-ventes':   'decisionnel:details',
  'detail-rec':      'decisionnel:details',
  'detail-pn':       'decisionnel:details',
  'duree-traitement':       'decisionnel:details',
  'situation-commerciale':  'decisionnel',
};

const DecisionnelPage = ({ user }) => {
  const d = useDecisionnel();

  /* ── Reliquat lots signés notaire — filtré par période sélectionnée ── */
  const lotsNotaire = useMemo(() => {
    if (!d.lotsRows?.length) return null;
    let rows = d.lotsRows.filter(r => r.statut_notaire === 'SIGNE');
    if (d.debut) rows = rows.filter(r => r.signe_notaire_date && r.signe_notaire_date.slice(0,10) >= d.debut);
    if (d.fin)   rows = rows.filter(r => r.signe_notaire_date && r.signe_notaire_date.slice(0,10) <= d.fin);
    const relFn = (arr) => arr.reduce((s, r) => s + (r.reliquat > 0 ? Number(r.reliquat) : 0), 0);
    const sumFn = (arr, k) => arr.reduce((s, r) => s + (Number(r[k]) || 0), 0);
    const prixNet = sumFn(rows, 'prix_net');
    const rel     = relFn(rows);
    const byNotaire = {};
    rows.forEach(r => {
      const nom = r.notaire_nom || 'Non assigné';
      if (!byNotaire[nom]) byNotaire[nom] = { nb: 0, rel: 0 };
      byNotaire[nom].nb  += 1;
      byNotaire[nom].rel += r.reliquat > 0 ? Number(r.reliquat) : 0;
    });
    const notaires = Object.entries(byNotaire)
      .sort((a, b) => b[1].rel - a[1].rel)
      .map(([nom, v]) => ({ nom, ...v }));
    return { nb: rows.length, prixNet, reliquat: rel,
      pct: prixNet > 0 ? ((prixNet - rel) / prixNet * 100) : null, notaires, rows };
  }, [d.lotsRows, d.debut, d.fin]);

  const canSeeSection = (id) => {
    if (user?.role === 'admin') return true;
    const privs = Array.isArray(user?.privileges) ? user.privileges : [];
    const decPrivs = privs.filter(p => p.startsWith('decisionnel:'));
    if (decPrivs.length === 0) return true; // aucune restriction → tout visible
    const needed = SECTION_PRIV[id];
    if (!needed) return true;
    return privs.includes(needed);
  };

  /* ── Print ──────────────────────────────────────────────────────────────── */
  const printRef = useRef(null);
  const [printOpen,        setPrintOpen]        = useState(false);
  const [printTitle,       setPrintTitle]       = useState('');
  const [printGroupBy,     setPrintGroupBy]     = useState('general');
  const [printOrientation, setPrintOrientation] = useState('landscape');
  const [printSections,    setPrintSections]    = useState(() => new Set(['kpi', 'projets', 'commerciaux', 'graphiques', 'analyse-rec', 'synthese', 'tableau']));

  /* ── Stock / Déstockage : filtre tranche par projet (clic carte) ─────────── */
  const [stockTrancheFilter, setStockTrancheFilter] = useState({});

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
      @page { size: A4 ${printOrientation}; margin: 15mm 12mm; }
      @page { @bottom-center { content: "Page " counter(page) " / " counter(pages); font-size: 8pt; color: #aaa; } }
    `,
  });

  const openPrint   = () => {
    const fmtD = s => s ? s.split('-').reverse().join('/') : '—';
    setPrintTitle(`Rapport Décisionnel — ${fmtD(d.debut)} → ${fmtD(d.fin)}`);
    setPrintOpen(true);
  };
  const closePrint  = () => setPrintOpen(false);
  const toggleSec   = id => setPrintSections(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const selectAll   = (list) => setPrintSections(new Set(list || ['kpi','notaire-soon','projets','commerciaux','ca-projet','graphiques','analyse-rec','synthese','tableau','detail-ventes','detail-rec','detail-pn','duree-traitement']));
  const selectNone  = () => setPrintSections(new Set());
  const toggleGroup = (list) => setPrintSections(prev => {
    const s = new Set(prev);
    const allIn = list.every(id => s.has(id));
    list.forEach(id => allIn ? s.delete(id) : s.add(id));
    return s;
  });
  const confirmPrint = () => { closePrint(); setTimeout(handlePrint, 100); };

  const SortIcon = ({ k }) => d.sortKey !== k ? null : d.sortKey === k && d.sortDir === -1
    ? <FaCaretDown size={10} style={{ marginLeft: 3 }} />
    : <FaCaretUp size={10} style={{ marginLeft: 3 }} />;

  const secProps = (id) => ({
    id, collapsed: !!d.sectionCollapsed[id], onToggle: d.toggleSec,
    dragging: d.dragSecId, dragOver: d.dragOverSecId,
    onDragStart: d.setDragSecId,
    onDragOver: (tid) => { if (tid !== d.dragSecId) d.setDragOverSecId(tid); },
    onDrop: (tid) => { d.moveSec(d.dragSecId, tid); d.setDragSecId(null); d.setDragOverSecId(null); },
    onDragEnd: () => { d.setDragSecId(null); d.setDragOverSecId(null); },
    filterPills: d.activeFilterPills,
    onScrollToFilters: d.scrollToFilters,
  });
  const secUnit  = (id) => d.sectionUnits?.[id] || 'default';
  const unitPick = (id) => <UnitPicker unit={secUnit(id)} onChange={u => d.setUnit(id, u)} />;

  return (
    <>
    <div style={{ minHeight: '100vh', background: C.light, padding: '0 0 40px' }}>
      <div style={{ padding: '16px 12px', maxWidth: 1400, margin: '0 auto' }}>

        {d.error && <Alert variant="danger" dismissible onClose={() => d.setError('')}>{d.error}</Alert>}

        {/* ── HEADER ─────────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
          <img src="/iconshems.png" alt="Shems" style={{ height: 40, objectFit: 'contain', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ fontWeight: 800, fontSize: '1.1rem', color: C.primary, lineHeight: 1.2 }}>Shems Dashboard</div>
            <div style={{ fontSize: '0.73rem', color: '#888', marginTop: 2 }}>Performance commerciale · Ventes · Recouvrement</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <button
              onClick={openPrint}
              title="Générer un rapport PDF"
              style={{ height: 36, padding: '0 14px', borderRadius: 7, cursor: 'pointer', background: C.light, color: C.primary, border: `1.5px solid ${C.border}`, fontSize: '0.78rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <FaPrint size={12} /> Rapport PDF
            </button>
            {d.lastFetch && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.58rem', color: '#bbb', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Synchro</div>
                <div style={{ fontSize: '0.68rem', color: '#999', fontWeight: 600 }}>
                  {new Date(d.lastFetch).toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', year:'2-digit', hour:'2-digit', minute:'2-digit' })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── FILTRES ────────────────────────────────────────────────────────── */}
        <div ref={d.filtersRef} style={{ background: '#fff', borderRadius: 10, border: `1px solid ${C.border}`, padding: '14px 18px', marginBottom: 20, boxShadow: '0 1px 4px rgba(109,78,54,0.06)' }}>
          {/* ligne 1 : dates + actions */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: '0.7rem', color: '#888', fontWeight: 600, display: 'block', marginBottom: 3 }}>Date début</label>
              <input type="date" value={d.inputDebut} onChange={e => d.setInputDebut(e.target.value)} style={{ ...inputStyle, width: 140 }} />
            </div>
            <div>
              <label style={{ fontSize: '0.7rem', color: '#888', fontWeight: 600, display: 'block', marginBottom: 3 }}>Date fin</label>
              <input type="date" value={d.inputFin} onChange={e => d.setInputFin(e.target.value)} style={{ ...inputStyle, width: 140 }} />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginLeft: 'auto' }}>
              {d.datesDirty && !d.loadK && (
                <span style={{ fontSize: '0.68rem', color: '#e65100', fontWeight: 700, background: '#fff3e0', border: '1px solid #ffb74d', borderRadius: 6, padding: '4px 10px' }}>
                  ⚠ Dates modifiées
                </span>
              )}
              <button
                onClick={() => { d.setDebut(d.inputDebut); d.setFin(d.inputFin); d.loadData(d.inputDebut, d.inputFin); }}
                disabled={d.loadK}
                style={{ height: 36, padding: '0 16px', borderRadius: 7, cursor: d.loadK ? 'not-allowed' : 'pointer', background: d.loadK ? '#b0896a' : d.datesDirty ? '#e65100' : C.primary, color: '#fff', border: 'none', fontSize: '0.8rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, opacity: d.loadK ? 0.8 : 1, transition: 'background 0.2s' }}>
                <FaSync size={11} style={d.loadK ? { animation: 'spin 1s linear infinite' } : {}} />
                {d.loadK ? 'Chargement…' : 'Actualiser'}
              </button>
              {d.hasFilters && (
                <button onClick={d.resetFilters} style={{ height: 36, padding: '0 14px', borderRadius: 7, cursor: 'pointer', background: '#fff', color: '#c62828', border: '1.5px solid #c62828', fontSize: '0.78rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FaTimes size={11} /> Réinitialiser
                </button>
              )}
            </div>
          </div>

          {/* ligne 2 : filtres */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
            <div style={{ minWidth: 140, flex: '1 1 140px', maxWidth: 200 }}>
              <label style={{ fontSize: '0.68rem', color: '#aaa', fontWeight: 600, display: 'block', marginBottom: 2 }}>VILLE</label>
              <Select isClearable placeholder="Toutes" value={d.villeOpt} onChange={d.setVilleOpt} options={toOpt(d.filters?.villes)} styles={selectStyles} isLoading={d.loadF} />
            </div>
            <div style={{ minWidth: 180, flex: '2 1 180px', maxWidth: 320 }}>
              <label style={{ fontSize: '0.68rem', color: '#aaa', fontWeight: 600, display: 'block', marginBottom: 2 }}>PROJET(S)</label>
              <Select isMulti isClearable placeholder="Tous" value={d.projetOpts} onChange={d.setProjetOpts} options={toOpt(d.projetsDisponibles)} styles={selectStyles} isLoading={d.loadF} />
            </div>
            <div style={{ minWidth: 130, flex: '1 1 130px', maxWidth: 190 }}>
              <label style={{ fontSize: '0.68rem', color: '#aaa', fontWeight: 600, display: 'block', marginBottom: 2 }}>TRANCHE</label>
              <Select isClearable placeholder="Toutes" value={d.trancheOpt} onChange={d.setTrancheOpt} options={toOptStr(d.filters?.tranches)} styles={selectStyles} isLoading={d.loadF} />
            </div>
            <div style={{ minWidth: 130, flex: '1 1 130px', maxWidth: 190 }}>
              <label style={{ fontSize: '0.68rem', color: '#aaa', fontWeight: 600, display: 'block', marginBottom: 2 }}>TYPE DE BIEN</label>
              <Select isClearable placeholder="Tous" value={d.typeOpt} onChange={d.setTypeOpt} options={toOptStr(d.filters?.types)} styles={selectStyles} isLoading={d.loadF} />
            </div>
            <div style={{ minWidth: 160, flex: '1 1 160px', maxWidth: 240 }}>
              <label style={{ fontSize: '0.68rem', color: '#aaa', fontWeight: 600, display: 'block', marginBottom: 2 }}>COMMERCIAL</label>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <Select isClearable placeholder="Tous" value={d.comOpt} onChange={d.setComOpt}
                    options={[
                      { value: 'all', label: 'Tous les commerciaux (objectifs cumulés)' },
                      ...toOpt(d.filters?.commerciaux, 'id', 'name'),
                    ]}
                    styles={selectStyles} isLoading={d.loadF} />
                </div>
                <button onClick={() => d.setComConfigOpen(v => !v)} title="Configurer l'affichage des commerciaux"
                  style={{ height: 34, width: 34, borderRadius: 7, flexShrink: 0, background: d.comConfigOpen ? C.primary : '#f5ede4', color: d.comConfigOpen ? '#fff' : C.primary, border: `1px solid ${C.border}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FaCog size={13} />
                </button>
              </div>
            </div>
            <div style={{ minWidth: 250, flex: '1 1 250px' }}>
              <label style={{ fontSize: '0.68rem', color: '#aaa', fontWeight: 600, display: 'block', marginBottom: 2 }}>TITRE FONCIER</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', minHeight: 34 }}>
                {TITRE_FONCIER_OPTIONS.map(opt => {
                  const active = d.titreOpt.some(o => o.value === opt.value);
                  return (
                    <button key={opt.value} type="button"
                      onClick={() => d.setTitreOpt(active ? d.titreOpt.filter(o => o.value !== opt.value) : [...d.titreOpt, opt])}
                      style={{
                        padding: '6px 12px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600,
                        cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
                        border: `1.5px solid ${active ? C.primary : C.border}`,
                        background: active ? C.primary : C.light,
                        color: active ? '#fff' : C.primary,
                      }}>
                      {opt.label}
                    </button>
                  );
                })}
                {d.titreOpt.length > 0 && (
                  <button type="button" onClick={() => d.setTitreOpt([])}
                    style={{ padding: '5px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', background: 'none', border: '1px solid #ccc', color: '#999' }}>
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Config commerciaux */}
          {d.comConfigOpen && (
            <div style={{ marginTop: 10, padding: '10px 14px', background: '#fdf8f4', border: `1px solid ${C.border}`, borderRadius: 8 }}>
              <div style={{ fontSize: '0.68rem', color: '#888', fontWeight: 600, marginBottom: 8 }}>Décocher pour regrouper dans "Autres"</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 18px', marginBottom: 8 }}>
                {(d.filters?.commerciaux || []).map(c => (
                  <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: '0.78rem', color: '#444' }}>
                    <input type="checkbox" checked={!d.comHidden.has(c.id)} onChange={() => d.toggleComHidden(c.id)} style={{ cursor: 'pointer', accentColor: C.primary }} />
                    {c.name}
                  </label>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => { d.setComHidden(new Set()); try { localStorage.removeItem('decisionnel_com_hidden'); } catch {} }} style={{ fontSize: '0.7rem', color: C.green, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>Tout afficher</button>
                <span style={{ color: '#ddd' }}>|</span>
                <button onClick={() => { const all = new Set((d.filters?.commerciaux || []).map(c => c.id)); d.setComHidden(all); try { localStorage.setItem('decisionnel_com_hidden', JSON.stringify([...all])); } catch {} }} style={{ fontSize: '0.7rem', color: '#c62828', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>Tout grouper</button>
              </div>
            </div>
          )}
        </div>

        {/* ── MODAL DÉTAIL ───────────────────────────────────────────────────── */}
        <KpiDetailPanel
          active={d.activeDetail}
          detailVentes={d.detailVentes} detailRec={d.detailRec} detailPn={d.detailPn} detailBudget={d.detailBudget} detailStockCa={d.detailStockCa} detailStockCumul={d.detailStockCumul} detailStockInitial={d.detailStockInitial}
          detailNS={d.detailNS} dureeData={d.dureeData}
          loadDV={d.loadDV} loadDR={d.loadDR} loadDP={d.loadDP} loadDB={d.loadDB} loadDSC={d.loadDSC} loadDSCu={d.loadDSCu} loadDSI={d.loadDSI} loadDNS={d.loadDNS} loadDuree={d.loadDuree}
          statutNotaireOpts={d.statutNotaireOpts} notaireOpts={d.notaireOpts} notairesList={d.notairesList}
          onClose={() => d.setActiveDetail(null)}
        />

        {/* ── SECTIONS ───────────────────────────────────────────────────────── */}
        {d.sectionOrder.map(id => {
          if (!canSeeSection(id)) return null;
          const sp = secProps(id);

          /* ── KPI Globaux ── */
          if (id === 'kpi') return (
            <SectionWrapper key={id} title={SEC_LABELS[id]} {...sp} unitPicker={unitPick(id)}>
              <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                <ComboKpiCard icon={<FaFileContract />} budgetLabel="Budget Vente" budgetValue={d.kpi?.budgetVente} realLabel="CA Com" realValue={d.kpi?.realisVente} pct={d.kpi?.pctVente} color={C.primary} loading={d.loadK} active={d.activeDetail?.type === 'ventes' && !d.activeDetail.rowType} unit={secUnit(id)}
                  budgetInfo="Objectif de CA commercial fixé sur la période, issu des budgets mensuels (goalV) saisis pour chaque commercial / projet."
                  realInfo="CA commercial réalisé = ventes directes (lots uniques) + parts d'associations vendues dans la période sélectionnée."
                  onBudgetClick={() => d.setActiveDetail({ type: 'budget', label: 'Détail Budget — Ventes' })}
                  onCardClick={() => d.setActiveDetail(prev => prev?.type === 'ventes' && !prev.rowType ? null : { type: 'ventes', label: 'CA Com — Toutes ventes' })}
                  details={d.kpi ? [
                    { label: 'Nb Comm', info: 'Nombre de ventes commercialisées : lots directs + fraction de lots en association (pondérée par le pourcentage de chaque part).', value: Number(d.kpi.nbComm || 0).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }), onClick: () => d.setActiveDetail(prev => prev?.type === 'ventes' && !prev.rowType ? null : { type: 'ventes', label: 'CA Com — Toutes ventes' }), activeRow: d.activeDetail?.type === 'ventes' && !d.activeDetail.rowType },
                    { label: 'CA Lots Uniques', info: 'Montant total des ventes directes (TYPE_VENTE = UNIQUE) dont la date de réservation est dans la période sélectionnée.', value: fmtUnit(d.kpi.caLotsUniques, secUnit(id)), color: C.primary, onClick: () => d.setActiveDetail(prev => prev?.rowType === 'contrat' ? null : { type: 'ventes', label: 'CA Com — Lots directs', rowType: 'contrat' }), activeRow: d.activeDetail?.rowType === 'contrat' },
                    { label: 'Nb Lots Uniques', info: 'Nombre de lots vendus directement (hors association) dans la période.', value: fmt(d.kpi.nbLotsUniques), onClick: () => d.setActiveDetail(prev => prev?.rowType === 'contrat' ? null : { type: 'ventes', label: 'CA Com — Lots directs', rowType: 'contrat' }), activeRow: d.activeDetail?.rowType === 'contrat' },
                    { label: 'CA Associations', info: 'Montant total des parts d\'association (PRIX_VENTE de chaque part) dont la date de vente est dans la période.', value: fmtUnit(d.kpi.caAssociations, secUnit(id)), color: C.blue, onClick: () => d.setActiveDetail(prev => prev?.rowType === 'association' ? null : { type: 'ventes', label: 'CA Com — Associations', rowType: 'association' }), activeRow: d.activeDetail?.rowType === 'association' },
                    { label: 'Nb Parts Asso', info: 'Nombre de parts d\'association vendues dans la période.', value: fmt(d.kpi.nbPartsAsso), onClick: () => d.setActiveDetail(prev => prev?.rowType === 'association' ? null : { type: 'ventes', label: 'CA Com — Associations', rowType: 'association' }), activeRow: d.activeDetail?.rowType === 'association' },
                  ] : []}
                />
                <ComboKpiCard icon={<FaHandshake />} budgetLabel="Budget Recouvrement" budgetValue={d.kpi?.budgetRec} realLabel="Enc_Recouv" realValue={d.kpi?.realisRec} pct={d.kpi?.pctRec} color={C.green} loading={d.loadK} unit={secUnit(id)}
                  budgetInfo="Objectif d'encaissement fixé sur la période, issu des budgets mensuels (goalE + goalR) des projets sélectionnés."
                  realInfo="Total de tous les encaissements (contrat_encaissement.PART) dont la date de règlement est dans la période et le statut est versé ou encaissé (hors IMPAYE)."
                  active={d.activeDetail?.type === 'recouvrement' && !d.activeDetail.statuts}
                  onBudgetClick={() => d.setActiveDetail({ type: 'budget', label: 'Détail Budget — Recouvrement' })}
                  onCardClick={() => d.setActiveDetail(prev => prev?.type === 'recouvrement' && !prev.statuts ? null : { type: 'recouvrement', label: 'Encaissements — Tous' })}
                  details={d.kpi ? [
                    { label: 'PTF', info: 'Chèques postdatés (PTF), réceptionnés (RECEPTION) et à ne pas verser (A_NE_PAS_VERSER_R, A_NE_PAS_VERSER_NR) — en attente de présentation ou de régularisation.', value: fmtUnit(d.kpi.encPtf, secUnit(id)), color: C.blue, onClick: () => d.setActiveDetail(prev => prev?.label === 'Encaissements — PTF' ? null : { type: 'recouvrement', label: 'Encaissements — PTF', statuts: ['RECEPTION','PTF','A_NE_PAS_VERSER_R','A_NE_PAS_VERSER_NR'] }), activeRow: d.activeDetail?.label === 'Encaissements — PTF' },
                    { label: 'Versé', info: 'Montants versés à la banque via EI, représentés ou en cours de versement — statuts EI, REPRESENTE, VERSEMENT.', value: fmtUnit(d.kpi.encVerse, secUnit(id)), color: '#6a1b9a', onClick: () => d.setActiveDetail(prev => prev?.label === 'Encaissements — Versé' ? null : { type: 'recouvrement', label: 'Encaissements — Versé', statuts: ['EI','REPRESENTE','VERSEMENT'] }), activeRow: d.activeDetail?.label === 'Encaissements — Versé' },
                    { label: 'Encaissé', info: 'Montants effectivement encaissés et confirmés par la banque — statuts NOTAIRE, ENCAISSE.', value: fmtUnit(d.kpi.encEncaise, secUnit(id)), color: C.green, onClick: () => d.setActiveDetail(prev => prev?.label === 'Encaissements — Encaissé' ? null : { type: 'recouvrement', label: 'Encaissements — Encaissé', statuts: ['NOTAIRE','ENCAISSE'] }), activeRow: d.activeDetail?.label === 'Encaissements — Encaissé' },
                    { label: 'Impayé', info: 'Chèques revenus impayés de la banque — statut IMPAYE. Montant à surveiller et à récupérer.', value: fmtUnit(d.kpi.encImpaye, secUnit(id)), color: '#c62828', onClick: () => d.setActiveDetail(prev => JSON.stringify(prev?.statuts) === JSON.stringify(['IMPAYE']) ? null : { type: 'recouvrement', label: 'Encaissements — Impayé', statuts: ['IMPAYE'] }), activeRow: JSON.stringify(d.activeDetail?.statuts) === JSON.stringify(['IMPAYE']) },
                  ] : []}
                />
                <ComboKpiCard icon={<FaBuilding />} budgetLabel="Budget PN" budgetValue={d.kpi?.budgetPn} realLabel="CA PN" realValue={d.kpi?.realisPn} pct={d.kpi?.pctPn} color={C.purple} loading={d.loadK} unit={secUnit(id)}
                  active={d.activeDetail?.type === 'pn'}
                  budgetInfo="Objectif de passages notaire (goalL) des projets sélectionnés."
                  realInfo="Montant total des ventes entrées en phase notaire dans la période."
                  onBudgetClick={() => d.setActiveDetail({ type: 'budget', label: 'Détail Budget — PN' })}
                  onCardClick={() => d.setActiveDetail(prev => prev?.type === 'pn' ? null : { type: 'pn', label: 'Passages Notaire' })}
                  details={d.kpi ? [
                    { label: 'Nb PN', info: 'Nombre de dossiers entrés en phase notaire dans la période sélectionnée (toutes sources de date confondues).', value: fmt(d.kpi.nbPn), color: C.purple, onClick: () => d.setActiveDetail(prev => prev?.type === 'pn' ? null : { type: 'pn', label: 'Passages Notaire' }), activeRow: d.activeDetail?.type === 'pn' },
                    { label: 'Lots chez notaire', info: 'Dossiers actuellement marqués "chez notaire" (ou en cours de signature) dont le bien n\'est pas encore signé notaire. Indépendant de la période sélectionnée.', value: fmt(d.kpi.pnActifNbLots), color: C.purple, onClick: () => d.setActiveDetail(prev => prev?.type === 'notaire-soon' && !prev.tab ? null : { type: 'notaire-soon', label: 'Prochainement au notaire' }), activeRow: d.activeDetail?.type === 'notaire-soon' && !d.activeDetail.tab },
                    { label: 'Avance totale', info: 'Somme de toutes les parts encaissées (contrat_encaissement.PART) sur les dossiers chez le notaire, quel que soit le statut du règlement.', value: fmtUnit(d.kpi.pnActifAvance, secUnit(id)), color: C.green },
                    { label: 'Reliquat total', info: 'Prix net total − Avance totale. Montant restant à encaisser sur l\'ensemble des dossiers actuellement chez le notaire.', value: fmtUnit(d.kpi.pnActifReliquat, secUnit(id)), color: '#c62828' },
                    { label: 'Versés',        info: 'Dossiers chez le notaire dont TOUS les encaissements ont un statut "versé" : EI, ENCAISSE ou REPRESENTE.', value: fmt(d.kpi.pnActifNbVerse),         color: C.green,   onClick: () => d.setActiveDetail(prev => prev?.tab === 'verse'         ? null : { type: 'notaire-soon', label: 'PN — Versés',         tab: 'verse' }),         activeRow: d.activeDetail?.tab === 'verse' },
                    { label: 'Encaissés',     info: 'Dossiers chez le notaire dont TOUS les encaissements sont confirmés par la banque (ENCAISSE ou REPRESENTE — EI exclu).', value: fmt(d.kpi.pnActifNbEncaisse),      color: C.blue,    onClick: () => d.setActiveDetail(prev => prev?.tab === 'encaisse'       ? null : { type: 'notaire-soon', label: 'PN — Encaissés',      tab: 'encaisse' }),       activeRow: d.activeDetail?.tab === 'encaisse' },
                    { label: 'Non versés',    info: 'Dossiers chez le notaire avec au moins un encaissement dont le statut n\'est pas encore EI, ENCAISSE ou REPRESENTE.', value: fmt(d.kpi.pnActifNbNonVerse),      color: '#c62828', onClick: () => d.setActiveDetail(prev => prev?.tab === 'non_verse'      ? null : { type: 'notaire-soon', label: 'PN — Non versés',     tab: 'non_verse' }),      activeRow: d.activeDetail?.tab === 'non_verse' },
                    { label: 'Proch. versés', info: 'Parmi les dossiers non versés, ceux dont au moins un règlement a une DATE_REGLEMENT prévue dans les 30 prochains jours.', value: fmt(d.kpi.pnActifNbProchainement), color: C.amber,   onClick: () => d.setActiveDetail(prev => prev?.tab === 'prochainement'  ? null : { type: 'notaire-soon', label: 'PN — Proch. versés',  tab: 'prochainement' }),  activeRow: d.activeDetail?.tab === 'prochainement' },
                    { label: 'Avec reliquat', info: 'Dossiers chez le notaire où le reliquat (Prix net − Avance) est supérieur à 10 DH — il reste des sommes à encaisser avant ou après signature.', value: fmt(d.kpi.pnActifNbReliquat),      color: C.amber,   onClick: () => d.setActiveDetail(prev => prev?.tab === 'reliquat'       ? null : { type: 'notaire-soon', label: 'PN — Avec reliquat',  tab: 'reliquat' }),       activeRow: d.activeDetail?.tab === 'reliquat' },
                  ] : []}
                />
                <ComboKpiCard
                  icon={<FaMoneyBillWave />}
                  color={C.amber}
                  loading={d.loadLots}
                  unit={secUnit(id)}
                  budgetLabel={`Signés notaire (${lotsNotaire?.nb ?? '—'} lots)`}
                  budgetInfo="Prix net total des contrats dont le bien est 'SIGNE NOTAIRE', filtrés par la date de signature notaire dans la période sélectionnée."
                  budgetValue={lotsNotaire?.prixNet}
                  realLabel="Reliquat à encaisser"
                  realInfo="Somme des reliquats positifs (Prix net − Avance) sur les lots signés notaire dans la période."
                  realValue={lotsNotaire?.reliquat}
                  pct={lotsNotaire?.pct}
                  active={d.activeDetail?.type === 'reliquat-notaire' && !d.activeDetail.notaire}
                  onCardClick={() => d.setActiveDetail(prev => prev?.type === 'reliquat-notaire' && !prev.notaire ? null : { type: 'reliquat-notaire', label: 'Reliquat — Tous lots signés notaire', rows: lotsNotaire?.rows || [] })}
                  details={lotsNotaire ? lotsNotaire.notaires.map((n, i) => {
                    const pct = lotsNotaire.reliquat > 0 ? Math.round(n.rel / lotsNotaire.reliquat * 100) : 0;
                    const pctColor = pct >= 50 ? '#c62828' : pct >= 25 ? '#e65100' : pct >= 10 ? '#1565c0' : '#2e7d32';
                    return {
                      label: `${n.nom} (${n.nb})`,
                      badge: { text: `${pct}%`, color: pctColor },
                      value: fmtUnit(n.rel, secUnit(id)),
                      color: [C.primary, C.blue, C.amber, C.green, '#6a1b9a', '#c62828'][i % 6],
                      onClick: () => d.setActiveDetail(prev => prev?.type === 'reliquat-notaire' && prev.notaire === n.nom ? null : { type: 'reliquat-notaire', label: `Reliquat — ${n.nom}`, notaire: n.nom, rows: (lotsNotaire?.rows || []).filter(r => (r.notaire_nom || 'Non assigné') === n.nom) }),
                      activeRow: d.activeDetail?.type === 'reliquat-notaire' && d.activeDetail.notaire === n.nom,
                    };
                  }) : []}
                />
              </div>

            </SectionWrapper>
          );

          /* ── Prochainement au Notaire ── */
          if (id === 'notaire-soon') return (
            <SectionWrapper key={id} title={SEC_LABELS[id]} {...sp} unitPicker={unitPick(id)}>
              <NotaireSoonSection
                rows={d.nsRows}
                loading={d.loadNS}
                multiProjet={d.projetOpts.length !== 1}
                unit={secUnit(id)}
              />
            </SectionWrapper>
          );

          /* ── Statut Lots Vendus Hors Notaire ── */
          if (id === 'statut-lots') return (
            <SectionWrapper key={id} title={SEC_LABELS[id]} {...sp} unitPicker={unitPick(id)}>
              <div style={{ background: '#fff', borderRadius: '0 0 10px 10px', padding: '14px 18px' }}>
                <StatutLotsSection rows={d.lotsRows} loading={d.loadLots} unit={secUnit(id)} />
              </div>
            </SectionWrapper>
          );

          /* ── Situation Encours ── */
          if (id === 'situation-encours') return (
            <SectionWrapper key={id} title={SEC_LABELS[id]} {...sp} unitPicker={unitPick(id)}>
              <div style={{ background: '#fff', borderRadius: '0 0 10px 10px', padding: '14px 18px' }}>
                <SituationEncoursSection rows={d.encoursRows} loading={d.loadEncours} unit={secUnit(id)} fin={d.fin} />
              </div>
            </SectionWrapper>
          );

          /* ── Commissions ── */
          if (id === 'commissions') return (
            <SectionWrapper key={id} title={SEC_LABELS[id]} {...sp}>
              <div style={{ background: '#fff', borderRadius: '0 0 10px 10px', padding: '14px 18px' }}>
                <CommissionSection
                  projetIds={d.projetOpts?.map(o => o.value) || []}
                  comId={d.comOpt?.value || null}
                />
              </div>
            </SectionWrapper>
          );

          /* ── Performance par Projet ── */
          if (id === 'projets') {
            if (!d.loadPK && d.projetKpi.length === 0) return null;
            const LIMIT_P = 6;
            const visibleP = d.projetKpi.filter(r => r.budgetVente > 0 || r.caVentes > 0 || r.realisRec > 0 || r.realisPn > 0);
            const displayedP = d.showAllProjets ? visibleP : visibleP.slice(0, LIMIT_P);
            return (
              <SectionWrapper key={id} title={SEC_LABELS[id]} {...sp} unitPicker={unitPick(id)}>
                <div style={{ padding: '12px 16px' }}>
                  {d.loadPK ? <div style={{ textAlign: 'center', padding: 20 }}><Spinner /></div> : (<>
                    <TableExportBar style={{ marginBottom: 8 }}
                      onExcel={() => exportExcel(d.projetKpi.map(r => ({ 'Projet': r.projet, 'Budget Vente': r.budgetVente, 'CA Ventes': r.caVentes, '% Vente': r.pctVente != null ? `${r.pctVente}%` : '—', 'Budget Recouv': r.budgetRec, 'Recouvrement': r.realisRec, '% Recouv': r.pctRec != null ? `${r.pctRec}%` : '—', 'Budget PN': r.budgetPn, 'CA PN': r.realisPn, '% PN': r.pctPn != null ? `${r.pctPn}%` : '—' })), 'kpi-par-projet')}
                      onCopy={() => { const u = secUnit(id); const { html, plain } = rowsToHtml(d.projetKpi.map(r => ({ 'Projet': r.projet, 'CA Ventes': fmtUnit(r.caVentes, u), '% V': r.pctVente != null ? `${r.pctVente}%` : '—', 'Recouvrement': fmtUnit(r.realisRec, u), '% R': r.pctRec != null ? `${r.pctRec}%` : '—', 'CA PN': fmtUnit(r.realisPn, u), '% PN': r.pctPn != null ? `${r.pctPn}%` : '—' }))); copyHtmlToClipboard(html, plain); }}
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 12 }}>
                      {displayedP.map((item, idx) => (
                        <ProjetKpiCard key={item.projetId} item={item} idx={idx} unit={secUnit(id)}
                          onBudgetClick={(projetId, projet) => d.setActiveDetail({ type: 'budget', label: `Budget — ${projet}`, projetId })}
                          onVentesClick={() => d.setActiveDetail({ type: 'ventes',       label: `CA Ventes — ${item.projet}`,       projetNom: item.projet })}
                          onRecClick={()    => d.setActiveDetail({ type: 'recouvrement', label: `Encaissements — ${item.projet}`,   projetNom: item.projet })}
                          onPnClick={()     => d.setActiveDetail({ type: 'pn',           label: `Passages Notaire — ${item.projet}`,projetNom: item.projet })}
                        />
                      ))}
                    </div>
                    {visibleP.length > LIMIT_P && (
                      <div style={{ textAlign: 'center', marginTop: 12 }}>
                        <button onClick={() => d.setShowAllProjets(v => !v)} style={{ background: 'none', border: '1px solid #8b5e3c', color: '#8b5e3c', borderRadius: 8, padding: '4px 18px', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600 }}>
                          {d.showAllProjets ? '▲ Moins' : `▼ Voir ${visibleP.length - LIMIT_P} autres projets`}
                        </button>
                      </div>
                    )}
                  </>)}
                </div>
              </SectionWrapper>
            );
          }

          /* ── Performance par Commercial ── */
          if (id === 'commerciaux') return (
            <SectionWrapper key={id} title={SEC_LABELS[id]} {...sp} unitPicker={unitPick(id)}>
              <ObjectifCommercialSection projetIds={d.filterProjetIds} finDate={d.fin} unit={secUnit(id)}
                projetLabel={d.projetOpts.length ? d.projetOpts.map(o => o.label).join(', ') : d.villeOpt ? `ville ${d.villeOpt.label}` : ''} />
            </SectionWrapper>
          );

          /* ── CA par Projet ── */
          if (id === 'ca-projet') {
            if (!d.loadPK && d.projetKpi.length === 0) return null;
            return (
              <SectionWrapper key={id} title={SEC_LABELS[id]} {...sp} unitPicker={unitPick(id)}>
                <div style={{ background: '#fff', borderRadius: '0 0 10px 10px', padding: '16px 20px' }}>
                  <TableExportBar style={{ marginBottom: 8 }}
                    onExcel={() => exportExcel(d.projetKpi.map(r => ({
                      'Projet':        r.projet,
                      'CA Ventes':     r.caVentes,
                      'Bud. Ventes':   r.budgetVente || 0,
                      '% Ventes':      r.pctVente != null ? `${r.pctVente}%` : '—',
                      'CA PN':         r.realisPn,
                      'Bud. PN':       r.budgetPn || 0,
                      '% PN':          r.pctPn    != null ? `${r.pctPn}%`    : '—',
                      'Recouvrement':  r.realisRec,
                      'Bud. Recouvr.': r.budgetRec || 0,
                      '% Recouvr.':    r.pctRec   != null ? `${r.pctRec}%`   : '—',
                      'Total Réalisé': r.caVentes + r.realisPn + r.realisRec,
                      'Total Budget':  (r.budgetVente || 0) + (r.budgetPn || 0) + (r.budgetRec || 0),
                    })), 'ca-par-projet')}
                    onCopy={() => {
                      const u = secUnit(id);
                      const pct = v => v != null ? `${v}%` : '—';
                      const { html, plain } = rowsToHtml(d.projetKpi.map(r => ({
                        'Projet':        r.projet,
                        'CA Ventes':     fmtUnit(r.caVentes, u),
                        'Bud. Ventes':   fmtUnit(r.budgetVente || 0, u),
                        '% V':           pct(r.pctVente),
                        'CA PN':         fmtUnit(r.realisPn, u),
                        'Bud. PN':       fmtUnit(r.budgetPn || 0, u),
                        '% PN':          pct(r.pctPn),
                        'Recouvr.':      fmtUnit(r.realisRec, u),
                        'Bud. Recouvr.': fmtUnit(r.budgetRec || 0, u),
                        '% R':           pct(r.pctRec),
                        'Total Réal.':   fmtUnit(r.caVentes + r.realisPn + r.realisRec, u),
                        'Total Bud.':    fmtUnit((r.budgetVente || 0) + (r.budgetPn || 0) + (r.budgetRec || 0), u),
                      })));
                      copyHtmlToClipboard(html, plain);
                    }}
                  />
                  <StackedBarProjet data={d.projetKpi} loading={d.loadPK} unit={secUnit(id)} />
                </div>
              </SectionWrapper>
            );
          }

          /* ── Graphiques Mensuels ── */
          if (id === 'graphiques') return (
            <SectionWrapper key={id} title={SEC_LABELS[id]} {...sp} unitPicker={unitPick(id)}>
              <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                {[
                  { title: 'CA Réalisé vs Budget (mensuel)', filename: 'ca-mensuel', realise: d.graphique?.realise || [], budget: d.graphique?.budget || [], color1: '#9e9e9e', color2: '#1565c0', dataType: 'ventes', excelLabel: 'Réalisé', excelBudgetLabel: 'Budget', copyScheme: 'blue', onCell: (m, dt) => d.setActiveDetail({ type: dt, label: `CA Ventes — ${fmtLabel(m)}`, month: m }), onBudget: (m) => d.setActiveDetail({ type: 'budget', label: `Budget Ventes — ${fmtLabel(m)}`, month: m }) },
                  { title: 'Évolution mensuelle de la livraison', filename: 'pn-mensuel', realise: d.graphique?.realisPn || [], budget: d.graphique?.budgetPn || [], color1: '#d4c09a', color2: '#1565c0', dataType: 'pn', excelLabel: 'Réalisé PN', excelBudgetLabel: 'Budget PN', copyScheme: 'brown', onCell: (m) => d.setActiveDetail({ type: 'pn', label: `Livraisons PN — ${fmtLabel(m)}`, month: m }), onBudget: (m) => d.setActiveDetail({ type: 'budget', label: `Budget PN — ${fmtLabel(m)}`, month: m }) },
                  { title: 'Évolution mensuelle des encaissements', filename: 'enc-mensuel', realise: d.graphique?.realisRec || [], budget: d.graphique?.budgetRec || [], color1: '#b07070', color2: '#1565c0', dataType: 'recouvrement', excelLabel: 'Réalisé Enc.', excelBudgetLabel: 'Budget Enc.', copyScheme: 'red', onCell: (m) => d.setActiveDetail({ type: 'recouvrement', label: `Encaissements — ${fmtLabel(m)}`, month: m }), onBudget: (m) => d.setActiveDetail({ type: 'budget', label: `Budget Recouv. — ${fmtLabel(m)}`, month: m }) },
                ].map(({ title, filename, realise, budget, color1, color2, dataType, excelLabel, excelBudgetLabel, copyScheme, onCell, onBudget }) => (
                  <div key={filename} style={{ background: '#fff', borderRadius: 10, border: `1px solid ${C.border}`, padding: '14px 16px', boxShadow: '0 1px 4px rgba(109,78,54,0.06)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={{ fontWeight: 700, color: C.primary, fontSize: '0.9rem' }}><FaChartBar size={13} style={{ marginRight: 6 }} />{title}</div>
                      {d.graphique && <Badge bg="none" style={{ background: C.light, color: C.primary, fontSize: '0.68rem', border: `1px solid ${C.border}` }}>{d.graphique.labels?.length} mois</Badge>}
                    </div>
                    <TableExportBar style={{ marginBottom: 8 }}
                      onExcel={() => {
                        const labels = d.graphique?.labels || [];
                        const toM = v => Number(((v || 0) / 1_000_000).toFixed(1));
                        const pct = (r, b) => b > 0 ? `${Math.round((r / b) * 100)}%` : '—';
                        const rows = labels.map((l, i) => ({
                          'Mois': fmtLabel(l),
                          [`${excelLabel} (MMAD)`]: toM(realise[i]),
                          [`${excelBudgetLabel} (MMAD)`]: toM(budget[i]),
                          '% Objectif': pct(realise[i] || 0, budget[i] || 0),
                        }));
                        const totR = realise.reduce((s, v) => s + (v || 0), 0);
                        const totB = budget.reduce((s, v) => s + (v || 0), 0);
                        rows.push({ 'Mois': 'TOTAL', [`${excelLabel} (MMAD)`]: toM(totR), [`${excelBudgetLabel} (MMAD)`]: toM(totB), '% Objectif': pct(totR, totB) });
                        exportExcel(rows, filename);
                      }}
                      onCopy={() => {
                        const labels = d.graphique?.labels || [];
                        const toM = v => { const n = ((v || 0) / 1_000_000).toFixed(1); return Number(n) === 0 ? '0' : n; };
                        const pct = (r, b) => b > 0 ? `${Math.round((r / b) * 100)}%` : '—';
                        const rows = labels.map((l, i) => ({
                          'Mois': fmtLabel(l),
                          [excelLabel]: toM(realise[i]),
                          [excelBudgetLabel]: toM(budget[i]),
                          '% Obj.': pct(realise[i] || 0, budget[i] || 0),
                        }));
                        const totR = realise.reduce((s, v) => s + (v || 0), 0);
                        const totB = budget.reduce((s, v) => s + (v || 0), 0);
                        rows.push({ 'Mois': 'TOTAL', [excelLabel]: toM(totR), [excelBudgetLabel]: toM(totB), '% Obj.': pct(totR, totB) });
                        const { html, plain } = rowsToHtml(rows, copyScheme);
                        copyHtmlToClipboard(html, plain);
                      }}
                    />
                    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <div style={{ flex: '0 0 auto', maxWidth: '100%', overflowX: 'auto' }}>
                        {!d.loadG && <TableauMensuel graphique={{ labels: d.graphique?.labels || [], realise, budget }} debut={d.debut} fin={d.fin} dataType={dataType} onCellClick={onCell} onBudgetClick={onBudget} unit={secUnit(id)} />}
                      </div>
                      <SvgExportBox filename={`${filename}-barres`} style={{ flex: '1 1 320px', minWidth: 0 }}>
                        <BarChart labels={d.graphique?.labels || []} realise={realise} budget={budget} loading={d.loadG} color1={color1} color2={color2} labelR="REALISATIONS" labelB="BUDGET" unit={secUnit(id)} />
                      </SvgExportBox>
                    </div>
                    {!d.loadG && d.graphique?.labels?.length > 0 && (
                      <SvgExportBox filename={`${filename}-cumul`}>
                        <CumulChart labels={d.graphique.labels} realise={realise} budget={budget} color1={color1} color2={color2} unit={secUnit(id)} />
                      </SvgExportBox>
                    )}
                  </div>
                ))}
              </div>
            </SectionWrapper>
          );

          /* ── Analyse Recouvrement ── */
          if (id === 'analyse-rec') return (
            <SectionWrapper key={id} title={SEC_LABELS[id]} {...sp} unitPicker={unitPick(id)}>
              <div style={{ padding: '0 0 4px' }}>
                <RecouvrementSection detailRec={d.detailRec} loadDR={d.loadDR} debut={d.debut} fin={d.fin}
                  ptfAvant={d.ptfAvant} ptfApres={d.ptfApres} ptfAvantRows={d.ptfAvantRows} ptfApresRows={d.ptfApresRows}
                  unit={secUnit(id)}
                  onStatutClick={(s) => d.setActiveDetail({ type: 'recouvrement', label: `Encaissements — ${s.label}`, statuts: s.values })}
                  onPtfBarClick={(bar) => d.setActiveDetail(prev => prev?.label === bar.label ? null : { type: 'recouvrement', ...bar })} />
              </div>
            </SectionWrapper>
          );

          /* ── Synthèse par Type ── */
          if (id === 'synthese') {
            if (!d.loadBPT && d.budgetParType.length === 0) return null;

            const typeLabels = { ventes: 'CA Ventes', recouvrement: 'Encaissements', pn: 'Livraisons PN' };

            const VILLE_COLORS = {
              casablanca: '#264061',
              marrakech:  '#964c4d',
              benslimane: '#013b23',
            };
            const PROJET_NUANCES = {
              '#264061': ['#264061', '#2e5073', '#1a3050', '#35628a'],
              '#964c4d': ['#964c4d', '#7d3f40', '#a85759', '#b96c6d'],
              '#013b23': ['#013b23', '#025e37', '#012a1a', '#026b41'],
            };
            const getVilleColor = (villeName) => {
              const n = (villeName || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
              return VILLE_COLORS[Object.keys(VILLE_COLORS).find(k => n.includes(k))] || null;
            };

            // ── Synthèse par projet (agrégation toutes types) ──────────────────
            const synProjetMap = {};
            d.budgetParType.forEach(r => {
              if (!synProjetMap[r.projetId]) synProjetMap[r.projetId] = {
                type: r.projet, projetId: r.projetId,
                nbV: 0, goalV: 0, caVentes: 0, realisRec: 0, nbL: 0, goalL: 0, realisPn: 0,
              };
              const p = synProjetMap[r.projetId];
              p.nbV += r.nbV; p.goalV += r.goalV; p.caVentes += r.caVentes;
              p.realisRec += r.realisRec; p.nbL += r.nbL; p.goalL += r.goalL; p.realisPn += r.realisPn;
            });
            Object.values(synProjetMap).forEach(p => {
              const bca = d.budgetParTypeCA?.[p.projetId];
              if (bca) { p.budgetCaV = bca.goalCaV; p.budgetCaE = bca.goalCaE; p.budgetCaL = bca.goalCaL; }
            });
            const synopsisRows = Object.values(synProjetMap).sort((a, b) => b.caVentes - a.caVentes);
            const synGlobalBCA = Object.values(d.budgetParTypeCA || {}).reduce(
              (acc, bca) => ({ goalCaV: acc.goalCaV + (bca.goalCaV || 0), goalCaE: acc.goalCaE + (bca.goalCaE || 0), goalCaL: acc.goalCaL + (bca.goalCaL || 0) }),
              { goalCaV: 0, goalCaE: 0, goalCaL: 0 }
            );
            const synGlobalBCAnull = synGlobalBCA.goalCaV > 0 ? synGlobalBCA : null;

            const byVille = {};
            d.budgetParType.forEach(r => {
              if (!byVille[r.villeId]) byVille[r.villeId] = { villeId: r.villeId, ville: r.ville, projets: {} };
              const v = byVille[r.villeId];
              if (!v.projets[r.projetId]) v.projets[r.projetId] = { projetId: r.projetId, projet: r.projet, rows: [] };
              v.projets[r.projetId].rows.push(r);
            });
            const villes = Object.values(byVille).sort((a, b) => a.ville.localeCompare(b.ville));

            return (
              <SectionWrapper key={id} title={SEC_LABELS[id]} {...sp} unitPicker={unitPick(id)}>
                <div style={{ background: '#fff', borderRadius: '0 0 10px 10px', padding: '14px 18px' }}>
                  {/* ── Synthèse globale par projet ── */}
                  {!d.loadBPT && synopsisRows.length > 0 && (
                    <div style={{ marginBottom: 28 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#363c48', letterSpacing: 0.2 }}>
                          Synthèse par projet
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <UnitPicker unit={secUnit(id)} onChange={u => d.setUnit(id, u)} />
                        <TableExportBar
                          onExcel={() => exportExcel(synopsisRows.map(r => ({
                            'Projet': r.type,
                            'Nb Ventes': r.nbV, 'Obj. Ventes': r.goalV, 'CA Ventes': r.caVentes, 'Bud. CA Ventes': r.budgetCaV || '',
                            'Encaissements': r.realisRec, 'Bud. Encaiss.': r.budgetCaE || '',
                            'Nb Livr.': r.nbL, 'Obj. Livr.': r.goalL, 'CA PN': r.realisPn, 'Bud. CA PN': r.budgetCaL || '',
                          })), 'synthese-par-projet')}
                          onCopy={() => {
                            const u = secUnit(id);
                            const { html, plain } = rowsToHtml(synopsisRows.map(r => ({
                              'Projet': r.type,
                              'Nb V': r.nbV, 'CA Ventes': fmtUnit(r.caVentes, u), 'Bud. CA V': r.budgetCaV ? fmtUnit(r.budgetCaV, u) : '—',
                              'Encaiss.': fmtUnit(r.realisRec, u), 'Bud. Encaiss.': r.budgetCaE ? fmtUnit(r.budgetCaE, u) : '—',
                              'Nb L': r.nbL, 'CA PN': fmtUnit(r.realisPn, u), 'Bud. CA PN': r.budgetCaL ? fmtUnit(r.budgetCaL, u) : '—',
                            })));
                            copyHtmlToClipboard(html, plain);
                          }}
                        />
                        </div>
                      </div>
                      <TableauSynthese
                        data={synopsisRows}
                        loading={false}
                        fin={d.fin}
                        budgetCA={synGlobalBCAnull}
                        unit={secUnit(id)}
                        onCellClick={null}
                      />
                      <hr style={{ borderColor: '#e0dbd4', margin: '24px 0 20px' }} />
                    </div>
                  )}
                  <TableExportBar style={{ marginBottom: 16 }}
                    onExcel={() => exportExcel(d.budgetParType.map(r => ({
                      'Ville': r.ville, 'Projet': r.projet, 'Type': r.type,
                      'Nb Ventes': r.nbV, 'Obj. Ventes': r.goalV, 'CA Ventes': r.caVentes, 'Bud. CA Ventes': r.budgetCaV || '',
                      'Encaissements': r.realisRec, 'Bud. Encaiss.': r.budgetCaE || '',
                      'Nb Livr.': r.nbL, 'Obj. Livr.': r.goalL, 'CA PN': r.realisPn, 'Bud. CA PN': r.budgetCaL || '',
                    })), 'synthese-par-type')}
                    onCopy={() => { const u = secUnit(id); const { html, plain } = rowsToHtml(d.budgetParType.map(r => ({
                      'Ville': r.ville, 'Projet': r.projet, 'Type': r.type,
                      'Nb V': r.nbV, 'CA Ventes': fmtUnit(r.caVentes, u), 'Bud. CA V': r.budgetCaV ? fmtUnit(r.budgetCaV, u) : '—',
                      'Encaiss.': fmtUnit(r.realisRec, u), 'Bud. Encaiss.': r.budgetCaE ? fmtUnit(r.budgetCaE, u) : '—',
                      'Nb L': r.nbL, 'CA PN': fmtUnit(r.realisPn, u), 'Bud. CA PN': r.budgetCaL ? fmtUnit(r.budgetCaL, u) : '—',
                    }))); copyHtmlToClipboard(html, plain); }}
                  />
                  {d.loadBPT ? (
                    <div style={{ textAlign: 'center', padding: 30 }}><Spinner /></div>
                  ) : villes.map(villeGroup => {
                    const vc = getVilleColor(villeGroup.ville);
                    const nuances = PROJET_NUANCES[vc] || [];
                    return (
                      <div key={villeGroup.villeId} style={{ marginBottom: 32 }}>
                        {/* ── Titre ville ── */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 10, borderBottom: `3px solid ${vc || C.primary}` }}>
                          <span style={{ fontSize: '1.05rem', fontWeight: 800, color: vc || C.primary, letterSpacing: 0.3 }}>
                            {villeGroup.ville}
                          </span>
                        </div>
                        {/* ── Tableaux par projet ── */}
                        {Object.values(villeGroup.projets).sort((a, b) => b.rows.reduce((s, r) => s + r.realisRec, 0) - a.rows.reduce((s, r) => s + r.realisRec, 0)).map((projetGroup, pi) => {
                          const pc = nuances[pi % nuances.length] || vc || '#5c3317';
                          return (
                            <div key={projetGroup.projetId} style={{ marginBottom: 24 }}>
                              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff', background: pc, border: `1px solid ${pc}`, borderRadius: '6px 6px 0 0', padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                                {projetGroup.projet}
                                <div style={{ marginLeft: 'auto' }}>
                                  <TableExportBar
                                    onExcel={() => {
                                      const bca = d.budgetParTypeCA?.[projetGroup.projetId];
                                      const tot = projetGroup.rows.reduce((a, r) => ({ nbV: a.nbV + r.nbV, goalV: a.goalV + r.goalV, caVentes: a.caVentes + r.caVentes, realisRec: a.realisRec + r.realisRec, nbL: a.nbL + r.nbL, goalL: a.goalL + r.goalL, realisPn: a.realisPn + r.realisPn }), { nbV: 0, goalV: 0, caVentes: 0, realisRec: 0, nbL: 0, goalL: 0, realisPn: 0 });
                                      exportExcel([
                                        ...projetGroup.rows.map(r => ({ 'Type': r.type, 'Nb Ventes': r.nbV, 'Obj. Ventes': r.goalV, 'CA Ventes': r.caVentes, 'Bud. CA Ventes': r.budgetCaV || '', 'Encaissements': r.realisRec, 'Bud. Encaiss.': r.budgetCaE || '', 'Nb Livr.': r.nbL, 'Obj. Livr.': r.goalL, 'CA PN': r.realisPn, 'Bud. CA PN': r.budgetCaL || '' })),
                                        { 'Type': 'Total', 'Nb Ventes': tot.nbV, 'Obj. Ventes': tot.goalV, 'CA Ventes': tot.caVentes, 'Bud. CA Ventes': bca?.goalCaV ?? '', 'Encaissements': tot.realisRec, 'Bud. Encaiss.': bca?.goalCaE ?? '', 'Nb Livr.': tot.nbL, 'Obj. Livr.': tot.goalL, 'CA PN': tot.realisPn, 'Bud. CA PN': bca?.goalCaL ?? '' },
                                      ], `synthese-${projetGroup.projet.toLowerCase().replace(/\s+/g,'-')}`);
                                    }}
                                    onCopy={() => {
                                      const u = secUnit(id);
                                      const bca = d.budgetParTypeCA?.[projetGroup.projetId];
                                      const tot = projetGroup.rows.reduce((a, r) => ({ nbV: a.nbV + r.nbV, caVentes: a.caVentes + r.caVentes, realisRec: a.realisRec + r.realisRec, nbL: a.nbL + r.nbL, realisPn: a.realisPn + r.realisPn }), { nbV: 0, caVentes: 0, realisRec: 0, nbL: 0, realisPn: 0 });
                                      const { html, plain } = rowsToHtml([
                                        ...projetGroup.rows.map(r => ({ 'Type': r.type, 'Nb V': r.nbV, 'CA Ventes': fmtUnit(r.caVentes, u), 'Bud. CA V': r.budgetCaV ? fmtUnit(r.budgetCaV, u) : '—', 'Encaiss.': fmtUnit(r.realisRec, u), 'Bud. Encaiss.': r.budgetCaE ? fmtUnit(r.budgetCaE, u) : '—', 'Nb L': r.nbL, 'CA PN': fmtUnit(r.realisPn, u), 'Bud. CA PN': r.budgetCaL ? fmtUnit(r.budgetCaL, u) : '—' })),
                                        { 'Type': 'Total', 'Nb V': tot.nbV, 'CA Ventes': fmtUnit(tot.caVentes, u), 'Bud. CA V': bca ? fmtUnit(bca.goalCaV, u) : '—', 'Encaiss.': fmtUnit(tot.realisRec, u), 'Bud. Encaiss.': bca ? fmtUnit(bca.goalCaE, u) : '—', 'Nb L': tot.nbL, 'CA PN': fmtUnit(tot.realisPn, u), 'Bud. CA PN': bca ? fmtUnit(bca.goalCaL, u) : '—' },
                                      ], 'brown');
                                      copyHtmlToClipboard(html, plain);
                                    }}
                                  />
                                </div>
                              </div>
                              <TableauSynthese
                                data={projetGroup.rows}
                                loading={false}
                                fin={d.fin}
                                budgetCA={d.budgetParTypeCA?.[projetGroup.projetId]}
                                unit={secUnit(id)}
                                onCellClick={(dtype, typeBien) => d.setActiveDetail({
                                  type: dtype,
                                  label: `${typeLabels[dtype] || dtype} — ${typeBien} (${projetGroup.projet})`,
                                  typeBien,
                                  projetNom: projetGroup.projet,
                                })}
                              />
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </SectionWrapper>
            );
          }

          /* ── Stock / Déstockage ── */
          if (id === 'stock') {
            if (!d.loadSD && d.stockDestockage.length === 0) return null;

            const VILLE_COLORS = {
              casablanca: '#264061',
              marrakech:  '#964c4d',
              benslimane: '#013b23',
            };
            const PROJET_NUANCES = {
              '#264061': ['#264061', '#2e5073', '#1a3050', '#35628a'],
              '#964c4d': ['#964c4d', '#7d3f40', '#a85759', '#b96c6d'],
              '#013b23': ['#013b23', '#025e37', '#012a1a', '#026b41'],
            };
            const getVilleColor = (villeName) => {
              const n = (villeName || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
              return VILLE_COLORS[Object.keys(VILLE_COLORS).find(k => n.includes(k))] || null;
            };

            const byVille = {};
            d.stockDestockage.forEach(r => {
              if (!byVille[r.villeId]) byVille[r.villeId] = { villeId: r.villeId, ville: r.ville, projets: {} };
              const v = byVille[r.villeId];
              if (!v.projets[r.projetId]) v.projets[r.projetId] = { projetId: r.projetId, projet: r.projet, rows: [] };
              v.projets[r.projetId].rows.push(r);
            });
            const villes = Object.values(byVille).sort((a, b) => a.ville.localeCompare(b.ville));

            return (
              <SectionWrapper key={id} title={SEC_LABELS[id]} {...sp} unitPicker={unitPick(id)}>
                <div style={{ background: '#fff', borderRadius: '0 0 10px 10px', padding: '14px 18px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: d.showStockFilters ? 6 : 12 }}>
                    <button onClick={() => d.setShowStockFilters(v => !v)}
                      style={{ background: 'none', border: '1px solid #d4c8bc', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', color: '#4a505c', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.7rem', fontWeight: 600 }}>
                      {d.showStockFilters ? <FaCaretUp size={11} /> : <FaCaretDown size={11} />}
                      Filtres
                    </button>
                  </div>
                  {d.showStockFilters && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12, fontSize: '0.78rem', color: '#555', flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none' }}>
                        <input type="checkbox" checked={d.includeOldSigned} onChange={e => d.setIncludeOldSigned(e.target.checked)} />
                        Inclure les anciens contrats signés notaire (sans date minute)
                      </label>
                      {d.includeOldSigned && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          avant le
                          <input type="date" value={d.oldSignedBefore} onChange={e => d.setOldSignedBefore(e.target.value)} style={{ ...inputStyle, width: 140, height: 28, fontSize: '0.76rem' }} />
                        </span>
                      )}
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600 }}>Type de bien :</span>
                        {(d.filters?.types || []).map((t, ti) => {
                          const active = d.stockTypeOpts.includes(t);
                          const col = getBienTypeColor(t);
                          return (
                            <button key={t}
                              onClick={() => d.setStockTypeOpts(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])}
                              style={{ background: active ? col : '#f5f4f2', color: active ? '#fff' : col, border: `1.5px solid ${col}`, borderRadius: 20, padding: '3px 12px', fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s' }}>
                              {t}
                            </button>
                          );
                        })}
                        <button onClick={() => d.setStockTypeOpts(d.filters?.types || [])} title="Tout sélectionner"
                          style={{ background: 'none', border: '1px solid #d4c8bc', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#2e7d32', display: 'flex', alignItems: 'center' }}>
                          <FaCheck size={10} />
                        </button>
                        {d.stockTypeOpts.length > 0 && (
                          <button onClick={() => d.setStockTypeOpts([])} title="Tout désélectionner"
                            style={{ background: 'none', border: '1px solid #d4c8bc', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#c62828', display: 'flex', alignItems: 'center' }}>
                            <FaTimes size={10} />
                          </button>
                        )}
                      </span>
                    </div>
                  )}
                  <TableExportBar style={{ marginBottom: 16 }}
                    onExcel={() => exportExcel(d.stockDestockage.map(r => ({
                      'Ville': r.ville, 'Projet': r.projet, 'Tranche': r.tranche, 'Type': r.type,
                      'Stock initial': r.stockInitial,
                      'Vendu/Déstocké (cumul)': r.cumulVendu,
                      '% Vendu/Livré': r.pctVenduLivre != null ? Math.round(r.pctVenduLivre) : '',
                      'Vendu/Déstocké (période)': r.periodeVendu,
                      'Chiffre d\'affaires': r.chiffreAffaire,
                    })), 'stock-destockage')}
                    onCopy={() => { const u = secUnit(id); const { html, plain } = rowsToHtml(d.stockDestockage.map(r => ({
                      'Ville': r.ville, 'Projet': r.projet, 'Tranche': r.tranche, 'Type': r.type,
                      'Stock': fmt(r.stockInitial),
                      'Vendu (cumul)': fmt(r.cumulVendu),
                      '% Vendu/Livré': r.pctVenduLivre != null ? `${Math.round(r.pctVenduLivre)}%` : '—',
                      'Vendu (période)': fmt(r.periodeVendu),
                      'CA': fmtUnit(r.chiffreAffaire, u),
                    }))); copyHtmlToClipboard(html, plain); }}
                  />
                  {d.loadSD ? (
                    <div style={{ textAlign: 'center', padding: 30 }}><Spinner /></div>
                  ) : villes.map(villeGroup => {
                    const vc = getVilleColor(villeGroup.ville);
                    const nuances = PROJET_NUANCES[vc] || [];
                    return (
                      <div key={villeGroup.villeId} style={{ marginBottom: 32 }}>
                        {/* ── Titre ville ── */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 10, borderBottom: `3px solid ${vc || C.primary}` }}>
                          <span style={{ fontSize: '1.05rem', fontWeight: 800, color: vc || C.primary, letterSpacing: 0.3 }}>
                            {villeGroup.ville}
                          </span>
                        </div>
                        {/* ── Tableaux par projet ── */}
                        {Object.values(villeGroup.projets).sort((a, b) => b.rows.reduce((s, r) => s + r.chiffreAffaire, 0) - a.rows.reduce((s, r) => s + r.chiffreAffaire, 0)).map((projetGroup, pi) => {
                          const pc = nuances[pi % nuances.length] || vc || '#5c3317';
                          const byTranche = {};
                          projetGroup.rows.forEach(r => {
                            const tk = r.tranche || '—';
                            if (!byTranche[tk]) byTranche[tk] = { tranche: tk, stockInitial: 0, cumulVendu: 0, periodeVendu: 0, chiffreAffaire: 0 };
                            byTranche[tk].stockInitial += r.stockInitial;
                            byTranche[tk].cumulVendu   += r.cumulVendu;
                            byTranche[tk].periodeVendu += r.periodeVendu;
                            byTranche[tk].chiffreAffaire += r.chiffreAffaire;
                          });
                          const trancheCards = Object.values(byTranche).sort((a, b) => a.tranche.localeCompare(b.tranche));
                          const activeTranche = stockTrancheFilter[projetGroup.projetId] || null;
                          const visibleRows = activeTranche ? projetGroup.rows.filter(r => (r.tranche || '—') === activeTranche) : projetGroup.rows;
                          return (
                            <div key={projetGroup.projetId} style={{ marginBottom: 24 }}>
                              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff', background: pc, border: `1px solid ${pc}`, borderRadius: '6px 6px 0 0', padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                                {projetGroup.projet}
                              </div>
                              {trancheCards.length > 1 && (
                                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', padding: '10px 0' }}>
                                  {trancheCards.map(t => {
                                    const pct = t.stockInitial > 0 ? (t.cumulVendu / t.stockInitial) * 100 : null;
                                    const isActive = activeTranche === t.tranche;
                                    return (
                                      <div key={t.tranche}
                                        onClick={() => setStockTrancheFilter(prev => ({ ...prev, [projetGroup.projetId]: prev[projetGroup.projetId] === t.tranche ? null : t.tranche }))}
                                        title={isActive ? 'Cliquer pour retirer le filtre' : `Filtrer sur la tranche ${t.tranche}`}
                                        style={{
                                          flex: '1 1 160px', minWidth: 150, background: isActive ? `${pc}14` : '#f9f8f6',
                                          border: `1px solid ${isActive ? pc : C.border}`, borderRadius: 8, padding: '8px 12px',
                                          cursor: 'pointer', boxShadow: isActive ? `0 0 0 2px ${pc}33` : 'none', transition: 'box-shadow 0.15s, border-color 0.15s',
                                        }}>
                                        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: pc, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          {t.tranche}
                                          {isActive && <FaTimes size={10} />}
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#666' }}>
                                          <span>Stock</span><span style={{ fontWeight: 700, color: '#333' }}>{fmt(t.stockInitial)}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#666' }}>
                                          <span>Vendu (cumul)</span><span style={{ fontWeight: 700, color: '#333' }}>{fmt(t.cumulVendu)} {pct != null ? `(${Math.round(pct)}%)` : ''}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#666' }}>
                                          <span>Vendu (période)</span><span style={{ fontWeight: 700, color: '#333' }}>{fmt(t.periodeVendu)}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#666' }}>
                                          <span>CA</span><span style={{ fontWeight: 700, color: C.primary }}>{fmtUnit(t.chiffreAffaire, secUnit(id))}</span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              <TableauStockDestockage
                                data={visibleRows}
                                loading={false}
                                unit={secUnit(id)}
                                onCellClick={(field, r) => {
                                  if (field === 'ca' || field === 'periode') {
                                    d.setActiveDetail(prev =>
                                      prev?.type === 'stock-ca' && prev.projetId === r.projetId && prev.typeBien === r.type && prev.tranche === r.tranche
                                        ? null
                                        : { type: 'stock-ca', label: `Vendu/Déstocké (période) — ${r.projet} — ${r.tranche} — ${r.type}`, projetId: r.projetId, typeBien: r.type, tranche: r.tranche }
                                    );
                                  } else if (field === 'cumul') {
                                    d.setActiveDetail(prev =>
                                      prev?.type === 'stock-cumul' && prev.projetId === r.projetId && prev.typeBien === r.type && prev.tranche === r.tranche
                                        ? null
                                        : { type: 'stock-cumul', label: `Vendu/Déstocké (cumul) — ${r.projet} — ${r.tranche} — ${r.type}`, projetId: r.projetId, typeBien: r.type, tranche: r.tranche }
                                    );
                                    d.fetchStockCumul(r.projetId, r.type, r.tranche);
                                  } else if (field === 'stock') {
                                    d.setActiveDetail(prev =>
                                      prev?.type === 'stock-initial' && prev.projetId === r.projetId && prev.typeBien === r.type && prev.tranche === r.tranche
                                        ? null
                                        : { type: 'stock-initial', label: `Stock initial — ${r.projet} — ${r.tranche} — ${r.type}`, projetId: r.projetId, typeBien: r.type, tranche: r.tranche }
                                    );
                                    d.fetchStockInitial(r.projetId, r.type, r.tranche);
                                  }
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </SectionWrapper>
            );
          }

          /* ── Détail par Projet / Type ── */
          if (id === 'tableau') return (
            <SectionWrapper key={id} title={SEC_LABELS[id]} {...sp} unitPicker={unitPick(id)}>
              <div style={{ background: '#fff', borderRadius: '0 0 10px 10px', overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, padding: '8px 16px', borderBottom: `1px solid ${C.border}` }}>
                  {!d.loadT && d.tableau.length > 0 && (<>
                    <XBtn onClick={() => exportExcel(d.sorted.map(r => ({ 'Projet': r.projet, 'Type': r.type, 'CA Total (MAD)': r.caTotal, 'Nb Ventes': r.nbVentes, 'CA PN (MAD)': r.caPn, 'Nb PN': r.nbPn, 'Recouvrement (MAD)': r.recouvrement })), 'detail-projet-type')} color="#2e7d32" bg="#e8f5e9" border="#a5d6a7">↓ Excel</XBtn>
                    <XBtn onClick={() => { const u = secUnit(id); const _r = d.sorted.map(r => ({ 'Projet': r.projet, 'Type': r.type || '—', 'CA Total': fmtUnit(r.caTotal, u), 'Nb Ventes': r.nbVentes, 'CA PN': fmtUnit(r.caPn, u), 'Nb PN': r.nbPn, 'Recouvrement': fmtUnit(r.recouvrement, u) })); _r.push({ 'Projet': 'TOTAL', 'Type': '', 'CA Total': fmtUnit(d.sorted.reduce((s,r)=>s+r.caTotal,0), u), 'Nb Ventes': d.sorted.reduce((s,r)=>s+r.nbVentes,0), 'CA PN': fmtUnit(d.sorted.reduce((s,r)=>s+r.caPn,0), u), 'Nb PN': d.sorted.reduce((s,r)=>s+r.nbPn,0), 'Recouvrement': fmtUnit(d.sorted.reduce((s,r)=>s+r.recouvrement,0), u) }); const { html, plain } = rowsToHtml(_r, 'brown'); copyHtmlToClipboard(html, plain); }} color="#1565c0" bg="#e3f2fd" border="#90caf9">⎘ Copier</XBtn>
                  </>)}
                  {d.loadT && <Spinner size="sm" />}
                </div>
                {d.loadT ? <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
                  : d.tableau.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: '#aaa', fontSize: '0.85rem' }}>Aucune donnée sur la période</div>
                  : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                        <thead>
                          <tr style={{ background: '#fdf8f4', borderBottom: `2px solid ${C.border}` }}>
                            {[{ key: 'projet', label: 'Projet' }, { key: 'type', label: 'Type' }, { key: 'caTotal', label: 'CA Total (MAD)' }, { key: 'nbVentes', label: 'Nb Ventes' }, { key: 'caPn', label: 'CA PN (MAD)' }, { key: 'nbPn', label: 'Nb PN' }, { key: 'recouvrement', label: 'Recouvrement (MAD)' }].map(({ key, label }) => (
                              <th key={key} onClick={() => d.toggleSort(key)} style={{ padding: '9px 14px', textAlign: key === 'projet' || key === 'type' ? 'left' : 'right', fontWeight: 700, color: d.sortKey === key ? C.primary : '#888', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                {label}<SortIcon k={key} />
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {d.sorted.map((row, i) => (
                            <tr key={`${row.projet}-${row.type}-${i}`} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? '#fff' : '#fdfaf7' }}
                              onMouseEnter={e => e.currentTarget.style.background = '#f9f3ed'}
                              onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fdfaf7'}>
                              <td style={{ padding: '8px 14px', color: C.primary, fontWeight: 600 }}>{row.projet}</td>
                              <td style={{ padding: '8px 14px', color: '#555' }}><Badge bg="none" style={{ background: C.light, color: C.primary, border: `1px solid ${C.border}`, fontSize: '0.7rem' }}>{row.type || '—'}</Badge></td>
                              <td style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 600 }}>{fmt(row.caTotal)}</td>
                              <td style={{ padding: '8px 14px', textAlign: 'right' }}>{row.nbVentes}</td>
                              <td style={{ padding: '8px 14px', textAlign: 'right', color: C.purple, fontWeight: 600 }}>{fmt(row.caPn)}</td>
                              <td style={{ padding: '8px 14px', textAlign: 'right', color: C.purple }}>{row.nbPn}</td>
                              <td style={{ padding: '8px 14px', textAlign: 'right', color: C.green, fontWeight: 600 }}>{fmt(row.recouvrement)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>{(() => {
                          const tot = { caTotal: d.sorted.reduce((s,r)=>s+r.caTotal,0), nbVentes: d.sorted.reduce((s,r)=>s+r.nbVentes,0), caPn: d.sorted.reduce((s,r)=>s+r.caPn,0), nbPn: d.sorted.reduce((s,r)=>s+r.nbPn,0), recouvrement: d.sorted.reduce((s,r)=>s+r.recouvrement,0) };
                          return <tr style={{ background: '#f5ede4', borderTop: `2px solid ${C.border}` }}><td colSpan={2} style={{ padding: '9px 14px', fontWeight: 700, color: C.primary, fontSize: '0.75rem' }}>TOTAL</td><td style={{ padding: '9px 14px', textAlign: 'right', fontWeight: 700 }}>{fmt(tot.caTotal)}</td><td style={{ padding: '9px 14px', textAlign: 'right', fontWeight: 700 }}>{tot.nbVentes}</td><td style={{ padding: '9px 14px', textAlign: 'right', fontWeight: 700, color: C.purple }}>{fmt(tot.caPn)}</td><td style={{ padding: '9px 14px', textAlign: 'right', fontWeight: 700, color: C.purple }}>{tot.nbPn}</td><td style={{ padding: '9px 14px', textAlign: 'right', fontWeight: 700, color: C.green }}>{fmt(tot.recouvrement)}</td></tr>;
                        })()}</tfoot>
                      </table>
                    </div>
                  )}
              </div>
            </SectionWrapper>
          );

          if (id === 'detail-ventes') return <SectionWrapper key={id} title={SEC_LABELS[id]} {...sp}><DetailTableVentes data={d.detailVentes} loading={d.loadDV} open={true} setOpen={() => {}} /></SectionWrapper>;
          if (id === 'detail-rec')    return <SectionWrapper key={id} title={SEC_LABELS[id]} {...sp}><DetailTableRec    data={d.detailRec}    loading={d.loadDR} open={true} setOpen={() => {}} /></SectionWrapper>;
          if (id === 'detail-pn')     return <SectionWrapper key={id} title={SEC_LABELS[id]} {...sp}><DetailTablePn     data={d.detailPn}     loading={d.loadDP} open={true} setOpen={() => {}} /></SectionWrapper>;

          if (id === 'duree-traitement') return (
            <SectionWrapper key={id} title={SEC_LABELS[id]} {...sp}>
              <DureeTraitementSection data={d.dureeData} loading={d.loadDuree}
                onKpiClick={({ band, label }) => d.setActiveDetail(prev => prev?.type === 'duree-traitement' && prev?.band === band ? null : { type: 'duree-traitement', label, band })} />
            </SectionWrapper>
          );

          /* ── Situation Commerciale ── */
          if (id === 'situation-commerciale') return (
            <SectionWrapper key={id} title={SEC_LABELS[id]} {...sp}>
              <div style={{ background: '#fff', borderRadius: '0 0 10px 10px', padding: '14px 18px' }}>
                <SituationCommercialeSection data={d.situationCommerciale} loading={d.loadSitComm} />
              </div>
            </SectionWrapper>
          );

          return null;
        })}

      </div>
    </div>

    {/* ── PRINT MODAL ──────────────────────────────────────────────────────── */}
    <PrintModal
      show={printOpen}
      onHide={closePrint}
      sections={printSections}
      onToggle={toggleSec}
      onToggleGroup={toggleGroup}
      onSelectAll={selectAll}
      onSelectNone={selectNone}
      title={printTitle}
      onTitleChange={setPrintTitle}
      groupBy={printGroupBy}
      onGroupByChange={setPrintGroupBy}
      orientation={printOrientation}
      onOrientationChange={setPrintOrientation}
      onPrint={confirmPrint}
    />

    {/* ── PRINT LAYOUT (hors écran, ciblé par react-to-print) ─────────────── */}
    <div style={{ position: 'fixed', top: 0, left: '-9999px', width: printOrientation === 'portrait' ? 794 : 1100, pointerEvents: 'none', zIndex: -1 }}>
      <PrintLayout ref={printRef} d={d} sections={printSections} title={printTitle} groupBy={printGroupBy} />
    </div>
    </>
  );
};

export default DecisionnelPage;
